import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Button, Typography } from "@mui/material";
import Loading from "../spinners/Loading";
import { useDispatch } from "react-redux";
import { setDialog } from "../../features/dialog/dialogSlice";
import ErrorAlert from "../alerts/ErrorAlert";
import { apiSlice } from "../../app/api/apiSlice";
import {
  useDeleteAllMembersMutation,
  useDeleteMemberMutation,
  useGetMembersQuery,
} from "../../features/members/membersApiSlice";

function DeleteMemberDialogContent({ dialogType, member }) {
  const { refetch } = useGetMembersQuery();
  const dispatch = useDispatch();

  const members = useMemo(() => {
    if (!member) return [];
    if (Array.isArray(member)) return member;
    if (Array.isArray(member?.members)) return member.members;
    return [member];
  }, [member]);

  const isDeleteAll = members.length === 0;
  const isBulkDelete = members.length > 1;

  const fullname =
    members.length === 1
      ? `${members[0]?.firstName ?? ""} ${members[0]?.lastName ?? ""}`.trim()
      : "";

  const [deleteAllMembers, { isLoading, isError }] =
    useDeleteAllMembersMutation();
  const [
    deleteMember,
    { isLoading: isDeletingMembers, isError: isDeleteError },
  ] = useDeleteMemberMutation();

  const handleDeleteMember = async () => {
    try {
      if (isDeleteAll) {
        await deleteAllMembers().unwrap();
      } else {
        const selectedIds = new Set(members.map((m) => String(m?._id)));

        const deletions = members.map((m) => {
          if (members.length === 1) {
            return deleteMember({ member: m, deleteContract: false }).unwrap();
          }

          const contractMembers = (m?.contractRef?.hasMembers || []).map((id) =>
            String(id?._id ?? id)
          );
          const shouldDeleteContract =
            contractMembers.length > 0 &&
            contractMembers.every((id) => selectedIds.has(String(id)));

          return deleteMember({
            member: m,
            deleteContract: shouldDeleteContract,
          }).unwrap();
        });

        await Promise.all(deletions);
      }
      const successMessage = isDeleteAll
        ? "Alle Mitglieder wurden erfolgreich gelöscht!"
        : isBulkDelete
        ? `${members.length} Mitglieder wurden erfolgreich gelöscht!`
        : fullname + " wurde erfolgreich gelöscht!";

      dispatch(
        setDialog({
          title: successMessage,
          type: "SUCCESS",
          data: {
            successText: successMessage,
          },
        })
      );
    } catch (error) {
      console.log("error :>> ", error);
      refetch();
    } finally {
      refetch();
      dispatch(apiSlice.util.resetApiState());
    }
  };

  if (isLoading || isDeletingMembers) {
    return (
      <div style={{ minHeight: 300 }}>
        <Loading />
      </div>
    );
  }

  if (isError || isDeleteError) {
    return (
      <ErrorAlert
        errorMessage={`Fehler beim Löschen ${
          isDeleteAll || isBulkDelete ? "der Mitglieder" : "des Mitglieds"
        }`}
      />
    );
  }

  return (
    <div>
      <Typography variant="h3" id="success-dialog-description">
        {isDeleteAll
          ? "Möchten Sie wirklich alle Mitglieder endgültig löschen?"
          : isBulkDelete
          ? "Möchten Sie diese Mitglieder wirklich endgültig löschen?"
          : "Möchten Sie dieses Mitglied wirklich endgültig löschen?"}
      </Typography>
      {!isDeleteAll && !isBulkDelete && (
        <table style={{ fontSize: "150%" }}>
          <tr>
            <th>Mitglied:</th>
            <th>{fullname}</th>
          </tr>
        </table>
      )}
      {isBulkDelete && (
        <Typography variant="h6" sx={{ mt: 2 }}>
          {members.length} Mitglieder ausgewählt
        </Typography>
      )}
      <Button
        onClick={handleDeleteMember}
        fullWidth
        variant="contained"
        color="error"
        sx={{
          mt: 2,
          mb: 3,
          fontSize: "125%",
        }}
      >
        Löschen
      </Button>
    </div>
  );
}

export default DeleteMemberDialogContent;

DeleteMemberDialogContent.propTypes = {
  dialogType: PropTypes.string,
  member: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};
